import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@monorepo/shared-auth';
import { IconPlus, IconFilter, IconSparkles } from '@tabler/icons-react';
import { getLeagues, getUserLeagues } from '../../services/leagueService';
import LeagueCard from '../../components/league/LeagueCard';
import CreateLeagueForm from '../../components/league/CreateLeagueForm';
import { SPORTS_LIST, LEAGUE_STATUS } from '../../data/leagueSports';

const Leagues = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('all');
  const [leagues, setLeagues] = useState([]);
  const [userLeagues, setUserLeagues] = useState([]);
  const [draftLeagues, setDraftLeagues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [filterSport, setFilterSport] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    loadLeagues();
  }, [currentUser, filterSport, filterStatus]);

  const loadLeagues = async () => {
    setLoading(true);
    try {
      const filters = {};
      if (filterSport) filters.sport = filterSport;
      if (filterStatus) filters.status = filterStatus;

      const [allLeagues, myLeagues] = await Promise.all([
        getLeagues(filters),
        currentUser ? getUserLeagues(currentUser.uid) : Promise.resolve([])
      ]);

      // Separate draft leagues from user's leagues
      const drafts = myLeagues.filter(league => league.status === LEAGUE_STATUS.DRAFT);
      const activeUserLeagues = myLeagues.filter(league => league.status !== LEAGUE_STATUS.DRAFT);

      // Filter out drafts from all leagues display
      const publicLeagues = allLeagues.filter(league => league.status !== LEAGUE_STATUS.DRAFT);

      setLeagues(publicLeagues);
      setUserLeagues(activeUserLeagues);
      setDraftLeagues(drafts);
    } catch (error) {
      console.error('Error loading leagues:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSuccess = (leagueId) => {
    setShowCreateModal(false);
    loadLeagues();
  };

  const handleCreateClick = () => {
    if (!currentUser) {
      navigate('/auth', { state: { returnTo: '/leagues', message: 'Sign in to create a league' } });
      return;
    }
    setShowCreateModal(true);
  };

  const getDisplayedLeagues = () => {
    switch (activeTab) {
      case 'my':
        return userLeagues;
      case 'drafts':
        return draftLeagues;
      default:
        return leagues;
    }
  };

  const displayedLeagues = getDisplayedLeagues();

  return (
    <main className="ub-leagues-page">
      <div className="ub-leagues-container">
        <div className="ub-leagues-header">
          <div className="ub-header-content">
            <h1>Leagues and Tournaments</h1>
            <p>Find and join competitive leagues or create your own</p>
          </div>

          <div className="ub-free-tier-banner">
            <IconSparkles size={20} />
            <span><strong>Free for leagues under 120 members</strong> — no credit card required</span>
          </div>

          <button
            onClick={handleCreateClick}
            className="ub-btn-create-league"
          >
            <IconPlus size={20} />
            <span>Create League</span>
          </button>
        </div>

        <div className="ub-leagues-tabs">
          <button
            className={`ub-tab ${activeTab === 'all' ? 'ub-active' : ''}`}
            onClick={() => setActiveTab('all')}
          >
            All Leagues
            {leagues.length > 0 && (
              <span className="ub-tab-count">{leagues.length}</span>
            )}
          </button>

          <button
            className={`ub-tab ${activeTab === 'my' ? 'ub-active' : ''}`}
            onClick={() => setActiveTab('my')}
          >
            My Leagues
            {userLeagues.length > 0 && (
              <span className="ub-tab-count">{userLeagues.length}</span>
            )}
          </button>

          <button
            className={`ub-tab ${activeTab === 'drafts' ? 'ub-active' : ''}`}
            onClick={() => setActiveTab('drafts')}
          >
            Drafts
            {draftLeagues.length > 0 && (
              <span className="ub-tab-count">{draftLeagues.length}</span>
            )}
          </button>

          <button
            className={`ub-btn-filter ${showFilters ? 'ub-active' : ''}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <IconFilter size={20} />
            <span>Filters</span>
          </button>
        </div>

        {showFilters && (
          <div className="ub-filters-panel">
            <div className="ub-filter-group">
              <label htmlFor="filterSport">Sport</label>
              <select
                id="filterSport"
                value={filterSport}
                onChange={(e) => setFilterSport(e.target.value)}
              >
                <option value="">All Sports</option>
                {SPORTS_LIST.map(sport => (
                  <option key={sport.id} value={sport.id}>
                    {sport.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="ub-filter-group">
              <label htmlFor="filterStatus">Status</label>
              <select
                id="filterStatus"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="">All Statuses</option>
                <option value={LEAGUE_STATUS.REGISTRATION}>Open for Registration</option>
                <option value={LEAGUE_STATUS.IN_PROGRESS}>In Progress</option>
                <option value={LEAGUE_STATUS.COMPLETED}>Completed</option>
              </select>
            </div>

            <button
              onClick={() => {
                setFilterSport('');
                setFilterStatus('');
              }}
              className="ub-btn-clear-filters"
            >
              Clear Filters
            </button>
          </div>
        )}

        <div className="ub-leagues-content">
          {loading ? (
            <div className="ub-loading-state">
              <div className="ub-loading-spinner"></div>
              <p>Loading leagues...</p>
            </div>
          ) : displayedLeagues.length > 0 ? (
            <div className="ub-leagues-grid">
              {displayedLeagues.map(league => (
                <LeagueCard key={league.id} league={league} />
              ))}
            </div>
          ) : (
            <div className="ub-empty-state">
              <div className="ub-empty-icon">🏆</div>
              <h3>
                {activeTab === 'drafts'
                  ? 'No drafts'
                  : activeTab === 'my'
                  ? 'No leagues yet'
                  : 'No leagues found'}
              </h3>
              <p>
                {activeTab === 'drafts'
                  ? 'Your draft leagues will appear here while you set them up'
                  : activeTab === 'my'
                  ? 'Join an existing league or create your own to get started'
                  : 'Be the first to create a league in this category!'}
              </p>
              <button
                onClick={handleCreateClick}
                className="ub-btn-create-empty"
              >
                <IconPlus size={20} />
                <span>Create League</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {showCreateModal && (
        <CreateLeagueForm
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleCreateSuccess}
        />
      )}
    </main>
  );
};

export default Leagues;
